// Central per-route metadata manager.
// Sets document.title, description, Open Graph, Twitter Card, and canonical URL
// on every SPA navigation so browser tabs, bookmarks, LinkedIn/Slack/Twitter
// unfurls, and search engines all see clean per-page metadata.
//
// One route → one entry. Keep this file the single source of truth.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const APP_NAME = "CareerOS";
const HOME_TITLE = "CareerOS | AI Career Operating System";
const HOME_DESC =
  "Your AI-powered career operating system. Resume, salary, roadmap, coach, and financial planner — built for the next 20 years, not the next job.";
const PROD_ORIGIN = "https://careeros-ai.com";
const OG_IMAGE = "/careeros-mark-256.png";

// ---- route-metadata registry ----------------------------------------------
// `title` is the raw, per-page label (already fully formatted). If a title
// contains "|" it's assumed to be complete; otherwise " | CareerOS" is
// appended automatically. `description` is used for meta description, OG
// description, and Twitter description.
const ROUTES = {
  // ----- public / marketing -----
  "/": {
    title: HOME_TITLE,
    description: HOME_DESC,
  },
  "/login": {
    title: "Log In | CareerOS",
    description: "Log in to your CareerOS account — pick up right where you left off with your resume, roadmap, and salary insights.",
  },
  "/signup": {
    title: "Create Your CareerOS Account",
    description: "Create your free CareerOS account and get an AI-generated career roadmap in under 5 minutes.",
  },
  "/forgot-password": {
    title: "Reset Password | CareerOS",
    description: "Reset your CareerOS password.",
  },
  "/reset-password": {
    title: "Reset Password | CareerOS",
    description: "Choose a new password for your CareerOS account.",
  },
  "/auth/callback": {
    title: "Signing you in | CareerOS",
    description: "Completing sign-in to CareerOS.",
  },
  "/directory": {
    title: "Talent Directory | CareerOS",
    description: "Explore public profiles of ambitious professionals building their careers on CareerOS.",
  },
  "/jobs-board": {
    title: "Job Search | CareerOS",
    description: "Discover open roles curated for your skills, salary target, and career trajectory.",
  },
  "/pricing": {
    title: "Pricing | CareerOS",
    description: "Simple pricing for individuals and teams — Free, Pro, and Premium plans.",
  },
  "/privacy": {
    title: "Privacy Policy | CareerOS",
    description: "How CareerOS-AI collects, uses, protects, and lets you control your personal data.",
    noindex: false,
  },
  "/terms": {
    title: "Terms of Service | CareerOS",
    description: "The terms and conditions that govern your use of CareerOS-AI.",
    noindex: false,
  },
  "/cookies": {
    title: "Cookie Policy | CareerOS",
    description: "How CareerOS-AI uses cookies and similar technologies, and how you can control them.",
    noindex: false,
  },
  "/ai-policy": {
    title: "AI Usage & Responsible AI Policy | CareerOS",
    description: "Where CareerOS-AI uses AI, which providers we use, what the AI is not for, and how we keep it safe and fair.",
    noindex: false,
  },
  "/subscription-policy": {
    title: "Subscription & Billing Policy | CareerOS",
    description: "How CareerOS-AI paid plans work — billing cycles, renewals, upgrades, downgrades, and cancellation.",
    noindex: false,
  },
  "/refund-policy": {
    title: "Refund Policy | CareerOS",
    description: "When CareerOS-AI subscription fees can be refunded, when they can't, and how to request a refund.",
    noindex: false,
  },
  "/acceptable-use": {
    title: "Acceptable Use Policy | CareerOS",
    description: "The ground rules that keep CareerOS-AI safe, useful, and trustworthy for everyone.",
    noindex: false,
  },
  "/disclaimer": {
    title: "Disclaimer | CareerOS",
    description: "CareerOS-AI provides educational content, not regulated legal, financial, tax, or medical advice. Read the full disclaimer.",
    noindex: false,
  },
  "/data-retention": {
    title: "Data Retention Policy | CareerOS",
    description: "How long CareerOS-AI keeps different categories of your data and when we delete it.",
    noindex: false,
  },
  "/accessibility": {
    title: "Accessibility Statement | CareerOS",
    description: "CareerOS-AI's commitment to WCAG 2.2 AA and AODA-conformant accessibility, known gaps, and how to give feedback.",
    noindex: false,
  },
  "/security": {
    title: "Security Policy | CareerOS",
    description: "How CareerOS-AI protects the confidentiality, integrity, and availability of your data — and how to responsibly disclose vulnerabilities.",
    noindex: false,
  },
  "/copyright": {
    title: "Copyright & Notice-and-Notice Policy | CareerOS",
    description: "How to report copyright or trademark infringement on CareerOS-AI under DMCA and Canada's Notice-and-Notice regime.",
    noindex: false,
  },
  "/payment/success": {
    title: "Payment Confirmed | CareerOS",
    description: "Your CareerOS subscription is active.",
    noindex: true,
  },
  "/unsubscribe/:token": {
    title: "Manage Email Preferences | CareerOS",
    description: "Update or unsubscribe from CareerOS emails.",
    noindex: true,
  },
  "/u/:username": {
    title: "Profile | CareerOS",
    description: "A public CareerOS professional profile.",
  },
  "/p/:username": {
    title: "Portfolio | CareerOS",
    description: "A public CareerOS portfolio.",
  },

  // ----- authenticated app shell -----
  "/app": {
    title: "Dashboard | CareerOS",
    description: "Your CareerOS dashboard — a single view of your resume score, salary progress, career roadmap, and next actions.",
    noindex: true,
  },
  "/app/profile": {
    title: "My Profile | CareerOS",
    description: "Manage your CareerOS profile, location, and preferences.",
    noindex: true,
  },
  "/app/resume": {
    title: "Resume Builder | CareerOS",
    description: "AI-powered resume builder with ATS scoring, keyword optimisation, and bullet rewriting.",
    noindex: true,
  },
  "/app/chat": {
    title: "AI Career Coach | CareerOS",
    description: "Chat with your CareerOS AI career coach — get personalised, locally-aware advice on jobs, salary, skills, and growth.",
    noindex: true,
  },
  "/app/salary": {
    title: "Salary Insights | CareerOS",
    description: "Data-driven salary benchmarks, negotiation ranges, and progression models — localised to your market.",
    noindex: true,
  },
  "/app/roadmap": {
    title: "Career Roadmap | CareerOS",
    description: "A step-by-step, AI-generated roadmap to reach your next career milestone.",
    noindex: true,
  },
  "/app/jobs": {
    title: "Job Search | CareerOS",
    description: "Personalised job matches ranked by fit, growth, and salary alignment.",
    noindex: true,
  },
  "/app/network": {
    title: "Network | CareerOS",
    description: "Track contacts, referrals, and outreach in one place.",
    noindex: true,
  },
  "/app/interview": {
    title: "AI Interview Coach | CareerOS",
    description: "Practice interviews with an AI coach that gives detailed, structured feedback on every answer.",
    noindex: true,
  },
  "/app/interview/live": {
    title: "Live Interview | CareerOS",
    description: "A live, voice-based mock interview with real-time AI coaching.",
    noindex: true,
  },
  "/app/interview/history": {
    title: "Interview History | CareerOS",
    description: "Review your past AI mock interviews and feedback.",
    noindex: true,
  },
  "/app/interview/session/:id": {
    title: "Interview Session | CareerOS",
    description: "Full playback and feedback for one AI mock interview.",
    noindex: true,
  },
  "/app/negotiation": {
    title: "Salary Negotiation | CareerOS",
    description: "Scripts, counter-offers, and prep for your next salary negotiation.",
    noindex: true,
  },
  "/app/financial": {
    title: "Financial Planner | CareerOS",
    description: "A career-linked financial planner — debt, emergency fund, savings, investments, and retirement modelling.",
    noindex: true,
  },
  "/app/executive": {
    title: "Executive Suite | CareerOS",
    description: "Executive-tier tools: board narrative, compensation strategy, and long-horizon career planning.",
    noindex: true,
  },
  "/app/insights": {
    title: "Career Insights | CareerOS",
    description: "AI-generated career insights tailored to your goals and market.",
    noindex: true,
  },
  "/app/notifications": {
    title: "Notifications | CareerOS",
    description: "All your CareerOS updates in one inbox.",
    noindex: true,
  },
  "/app/certifications": {
    title: "Certifications | CareerOS",
    description: "Recommended certifications ranked by ROI, local recognition, and career-impact potential.",
    noindex: true,
  },
  "/app/achievements": {
    title: "Achievements | CareerOS",
    description: "Track your career milestones, streaks, and unlocked achievements.",
    noindex: true,
  },
  "/app/referrals": {
    title: "Referrals | CareerOS",
    description: "Invite friends and earn CareerOS credits.",
    noindex: true,
  },
  "/app/admin": {
    title: "Admin Dashboard | CareerOS",
    description: "CareerOS internal admin dashboard.",
    noindex: true,
  },
  "/app/admin/support": {
    title: "Admin — Support | CareerOS",
    description: "Support case queue and knowledge-base management.",
    noindex: true,
  },
  "/app/admin/email": {
    title: "Admin — Email | CareerOS",
    description: "Email templates, deliverability, and unsubscribe management.",
    noindex: true,
  },
  "/app/support": {
    title: "Support | CareerOS",
    description: "Get help with your CareerOS account, subscription, or features.",
    noindex: true,
  },
  "/app/assessments": {
    title: "Career Assessments | CareerOS",
    description: "Take AI-driven career, skills, and personality assessments.",
    noindex: true,
  },
  "/app/assessments/:type": {
    title: "Assessment | CareerOS",
    description: "Take a CareerOS assessment.",
    noindex: true,
  },
  "/app/assessments/:type/:mode": {
    title: "Assessment | CareerOS",
    description: "Take a CareerOS assessment.",
    noindex: true,
  },
  "/app/portfolio": {
    title: "Portfolio | CareerOS",
    description: "Build and share your public CareerOS portfolio.",
    noindex: true,
  },
  "/app/recruiter": {
    title: "Recruiter Hub | CareerOS",
    description: "Recruiter-facing tools for outreach and candidate discovery.",
    noindex: true,
  },
  "/app/settings": {
    title: "Settings | CareerOS",
    description: "Account, notifications, billing, and privacy settings.",
    noindex: true,
  },
  "/app/pricing": {
    title: "Pricing | CareerOS",
    description: "Upgrade to CareerOS Pro or Premium.",
    noindex: true,
  },
};

// ---- pattern-matching helpers ---------------------------------------------
function toRegex(pattern) {
  const src = "^" + pattern.replace(/:[^/]+/g, "[^/]+") + "$";
  return new RegExp(src);
}

const ORDERED = Object.keys(ROUTES)
  .sort((a, b) => {
    // More slashes → more specific first.
    const sa = (a.match(/\//g) || []).length;
    const sb = (b.match(/\//g) || []).length;
    if (sb !== sa) return sb - sa;
    // Fewer params → more specific first.
    const pa = (a.match(/:/g) || []).length;
    const pb = (b.match(/:/g) || []).length;
    return pa - pb;
  })
  .map((k) => ({ pattern: k, re: toRegex(k), ...ROUTES[k] }));

export function resolveRouteMeta(pathname) {
  for (const entry of ORDERED) {
    if (entry.re.test(pathname)) return entry;
  }
  return null;
}

// ---- DOM helpers -----------------------------------------------------------
function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tag || "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === "tag") return;
    if (v == null) return;
    if (k === "text") el.textContent = String(v);
    else el.setAttribute(k, String(v));
  });
  return el;
}

function upsertOgMeta(property, content) {
  upsertMeta(`meta[property="${property}"]`, { property, content });
}
function upsertNamedMeta(name, content) {
  upsertMeta(`meta[name="${name}"]`, { name, content });
}
function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// ---- component -------------------------------------------------------------
export default function TitleManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = resolveRouteMeta(pathname) || {};
    const rawTitle = meta.title || HOME_TITLE;
    // Titles that already include the app name are used as-is; short labels
    // get " | CareerOS" auto-appended. (Home + Signup are pass-through.)
    const title = rawTitle.includes("|") || /CareerOS/i.test(rawTitle)
      ? rawTitle
      : `${rawTitle} | ${APP_NAME}`;
    const description = meta.description || HOME_DESC;

    // Prefer the production canonical origin so search engines and social
    // unfurls point at the live site even when browsed from the preview.
    const canonical = PROD_ORIGIN + pathname;

    // 1. Title
    document.title = title;

    // 2. Standard description
    upsertNamedMeta("description", description);

    // 3. Open Graph
    upsertOgMeta("og:title", title);
    upsertOgMeta("og:description", description);
    upsertOgMeta("og:url", canonical);
    upsertOgMeta("og:image", PROD_ORIGIN + OG_IMAGE);
    upsertOgMeta("og:type", "website");
    upsertOgMeta("og:site_name", APP_NAME);

    // 4. Twitter Card
    upsertNamedMeta("twitter:card", "summary");
    upsertNamedMeta("twitter:title", title);
    upsertNamedMeta("twitter:description", description);
    upsertNamedMeta("twitter:image", PROD_ORIGIN + OG_IMAGE);

    // 5. Canonical + robots
    upsertCanonical(canonical);
    upsertNamedMeta("robots", meta.noindex ? "noindex,nofollow" : "index,follow");
  }, [pathname]);

  return null;
}
